import React from 'react';
import { DefaultColumnFilter } from 'src/components/shared/TPEReactTable';
import { ColumnDef, SegmentRow } from 'src/models/segment-cdt/SegmentCdtTypes';

/**
 * Pure builders for the segment detail data grid — kept out of the component so column shaping and row
 * flattening are unit-testable without mounting. The grid's columns are the four row-level FRAMES
 * fields followed by one column per `ColumnDef`; a row's cell value is looked up by `columnId` from the
 * numeric `values` map or the `textValues` map (a column lives in exactly one of them).
 */

/** Row-level identity/frame fields, in display order, ahead of the per-table value columns. */
const FIXED_COLUMNS: { accessor: string; header: string }[] = [
    { accessor: 'segmentValue', header: 'Segment Value' },
    { accessor: 'segmentName', header: 'Segment Name' },
    { accessor: 'segmentDescription', header: 'Segment Description' },
    { accessor: 'status', header: 'Status' },
];

/** Numbers get thousands separators; text renders as-is; missing cells render blank. */
function renderCell(value: any): JSX.Element {
    const text = value == null ? '' : typeof value === 'number' ? value.toLocaleString() : String(value);
    return <div className="cell-text">{text}</div>;
}

/** react-table column defs: the four fixed columns, then one per `ColumnDef` keyed by `columnId`. */
export function buildColumnDefinitions(columns: ColumnDef[] = []): any[] {
    // Filter/hidePopover let the "Filter rows" toggle render an inline per-column filter input.
    const fixed = FIXED_COLUMNS.map((c) => ({
        accessor: c.accessor,
        Header: () => <div className="cell-text">{c.header}</div>,
        Cell: (e: any) => renderCell(e.value),
        Filter: DefaultColumnFilter,
        hidePopover: true,
    }));
    const dynamic = columns
        .filter((c) => !!c.columnId)
        .map((c) => ({
            accessor: c.columnId as string,
            Header: () => <div className="cell-text">{c.displayName ?? c.columnId}</div>,
            Cell: (e: any) => renderCell(e.value),
            Filter: DefaultColumnFilter,
            hidePopover: true,
        }));
    return [...fixed, ...dynamic];
}

/** Flatten a wire `SegmentRow` into the plain row object the grid renders (values keyed by columnId). */
export function flattenRow(row: SegmentRow, columns: ColumnDef[] = []): Record<string, any> {
    const flat: Record<string, any> = {
        segmentValue: row.segmentValue,
        segmentName: row.segmentName,
        segmentDescription: row.segmentDescription,
        status: row.status,
    };
    columns.forEach((c) => {
        const id = c.columnId;
        if (!id) {
            return;
        }
        const numeric = row.values ? row.values[id] : undefined;
        const text = row.textValues ? row.textValues[id] : undefined;
        flat[id] = numeric != null ? numeric : text != null ? text : undefined;
    });
    return flat;
}
